import * as React from "react";

import { useTripleWhale } from "../../../Common";
import {
  buildSelectItemProperties,
  buildViewItemListProperties,
} from "../../../Common/analytics/triplewhale/custom-events";
import { useReactPlpInitialState } from "../react-plp-intial-state-provider";
import { ResponseProduct, ResponseProductsSearch } from "../../model";

import { ReactPlpAnalyticsInterface } from "./analytics-interface";

/**
 * Triple Whale implementation of the PLP analytics interface (BBNT_TripleWhaleEvents,
 * docs/tw-events-plan.md §5 rows 3 and 10) over the Fredhopper listing: the custom events
 * `view_item_list` on every listing render (fresh results, load more, load previous) and
 * `select_item` on a tile click. Ids are Magento entity ids (`magento_id_product`), never the
 * Fredhopper id. A `select_item` keeps the listed product as `item_id` (the id its
 * `view_item_list` carried) and reports the product the tile opens as `variant_id`: the
 * grouped product of the selected colour, which differs from the listed product after a
 * swatch choice or on a style tile listed by its wrapper. Banner rows occupy result slots
 * without a Magento product and are not items. The list is the category (last breadcrumb)
 * or the search results, as in the GA4 events.
 *
 * M2-2047: the analytics configuration loads asynchronously (GraphQL), like the listing, so
 * a listing can be shown before it has loaded. Such a listing is not dropped: it waits and
 * goes out, in order, once the configuration has loaded - through the same Triple Whale hook,
 * so a disabled pixel or switched-off custom events still send nothing. The methods are
 * therefore not hidden behind consumerInitializationGuard.
 */

interface ItemListMetadata {
  listId: string;
  listName: string;
  pageType: "category" | "search";
}

const isListingProduct = (product: ResponseProduct): boolean =>
  typeof product.magento_id_product === "string" &&
  product.magento_id_product !== "";

const resolveItemList = (
  initialState: { baseQuery?: { cat_id?: string; search?: string }; pageConfiguration?: { pageTitle?: string } },
  breadcrumbs?: ResponseProductsSearch["breadcrumbs"]
): ItemListMetadata => {
  // page origin, not the refinable query, is the stable discriminator (no raw search
  // string as an id: high cardinality, may carry personal data)
  if (Object.prototype.hasOwnProperty.call(initialState.baseQuery || {}, "search")) {
    return { listId: "search-results", listName: "Search results", pageType: "search" };
  }
  const crumb = breadcrumbs?.[breadcrumbs.length - 1];
  if (crumb?.id && crumb.name) {
    return { listId: crumb.id, listName: crumb.name, pageType: "category" };
  }
  return {
    listId: `category:${initialState.baseQuery?.cat_id || ""}`,
    listName: initialState.pageConfiguration?.pageTitle || "Category results",
    pageType: "category",
  };
};

export const useAnalyticsTripleWhale = (): ReactPlpAnalyticsInterface & {
  hasInitialized: boolean;
} => {
  const { isConfigurationLoaded, fireCustom } = useTripleWhale();
  const { initialState } = useReactPlpInitialState();
  // the list of the listing rendered last: a click reports the same list as the view
  // that showed the tile (the click packet carries no breadcrumbs)
  const lastList = React.useRef<ItemListMetadata | null>(null);
  // listings waiting to be sent, oldest first
  const pendingViews = React.useRef<Array<ReturnType<typeof buildViewItemListProperties>>>([]);

  const flushViews = React.useCallback(() => {
    while (pendingViews.current.length > 0) {
      const properties = pendingViews.current.shift();
      if (properties) {
        void fireCustom("view_item_list", properties);
      }
    }
  }, [fireCustom]);

  const onViewFredhopperListingPage: NonNullable<
    ReactPlpAnalyticsInterface["onViewFredhopperListingPage"]
  > = React.useCallback(
    ({ initialStateSearchTerms, fredhopperResponse }) => {
      const list = resolveItemList(initialState, fredhopperResponse.breadcrumbs);
      lastList.current = list;
      // always queued first, so a listing shown just as the configuration loads cannot
      // overtake older ones still waiting for it
      pendingViews.current.push(
        buildViewItemListProperties({
          ...list,
          itemIds: (fredhopperResponse.products || [])
            .filter(isListingProduct)
            .map((product) => product.magento_id_product),
          searchTerm:
            list.pageType === "search"
              ? initialStateSearchTerms || undefined
              : undefined,
        })
      );
      if (isConfigurationLoaded) {
        flushViews();
      }
    },
    [initialState, isConfigurationLoaded, flushViews]
  );

  React.useEffect(() => {
    if (isConfigurationLoaded) {
      flushViews();
    }
  }, [isConfigurationLoaded, flushViews]);

  const clickActionOnProduct: NonNullable<
    ReactPlpAnalyticsInterface["clickActionOnProduct"]
  > = React.useCallback(
    ({ presentationProduct, productIndex, openedProductId }) => {
      if (!isListingProduct(presentationProduct)) {
        return;
      }
      const list = lastList.current || resolveItemList(initialState);
      void fireCustom(
        "select_item",
        buildSelectItemProperties({
          itemId: presentationProduct.magento_id_product,
          variantId: openedProductId,
          listId: list.listId,
          listName: list.listName,
          position: typeof productIndex === "number" ? productIndex + 1 : 0,
        })
      );
    },
    [initialState, fireCustom]
  );

  return React.useMemo(
    () => ({
      hasInitialized: isConfigurationLoaded,
      onViewFredhopperListingPage,
      clickActionOnProduct,
    }),
    [isConfigurationLoaded, onViewFredhopperListingPage, clickActionOnProduct]
  );
};
